import React, { useState, useRef } from "react";
import { Modal } from "../../../components/ui/Modal";
import { Pill } from "../../../components/ui/Pill";
import { TYPES } from "../../../constants/wineTypes";

interface ParsedWine {
  type: string;
  name: string;
  mil: string;
  dom: string;
  prix: string;
  _typeOk: boolean;
}

interface WineImportModalProps {
  // May return the number of wines actually created (server-side duplicates
  // are silently skipped, so this can be lower than the number sent). If the
  // caller doesn't return a number, we assume every valid row was created.
  onImport: (wines: { type: string; name: string; mil: string; dom: string; prix: string }[]) => Promise<number | void> | number | void;
  onClose: () => void;
}

const TYPE_IDS = TYPES.map((t) => t.id);

// "moelleux" (and similar sweet-wine wording) is not its own category anymore:
// treat it as "blanc" so imported rows aren't dropped as invalid, matching
// the normalization done in ParseCSV.ts / WineRepository.ts on the backend.
const SWEET_TYPE_SYNONYMS = new Set(["moelleux", "liquoreux", "doux", "sweet"]);

// Permissive CSV split: supports comma or semicolon as separator,
// and trims surrounding quotes/whitespace.
function splitRow(line: string): string[] {
  const sep = line.includes(";") && !line.includes(",") ? ";" : ",";
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === "\"") {
      inQuotes = !inQuotes;
      continue;
    }
    if (c === sep && !inQuotes) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

const HEADERS_MAP: Record<string, keyof ParsedWine> = {
  type: "type", categorie: "type", "catégorie": "type", couleur: "type", color: "type",
  name: "name", nom: "name", appellation: "name", cuvee: "name", "cuvée": "name",
  mil: "mil", millesime: "mil", "millésime": "mil", year: "mil", annee: "mil", "année": "mil", vintage: "mil",
  dom: "dom", domaine: "dom", producteur: "dom", domain: "dom", producer: "dom",
  prix: "prix", price: "prix", tarif: "prix",
};

function parseCsv(raw: string): ParsedWine[] {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return [];
  const headerCells = splitRow(lines[0]).map((h) => h.toLowerCase());
  // Detect if first row is a header by checking that at least 3 columns map to known fields.
  const mapped = headerCells.map((h) => HEADERS_MAP[h]);
  const hasHeader = mapped.filter(Boolean).length >= 3;
  const startIdx = hasHeader ? 1 : 0;
  // Default column order if no header: type, name, mil, dom, prix
  const columnKeys: (keyof ParsedWine | undefined)[] = hasHeader
    ? mapped
    : ["type", "name", "mil", "dom", "prix"];

  const result: ParsedWine[] = [];
  for (let i = startIdx; i < lines.length; i++) {
    const cells = splitRow(lines[i]);
    if (cells.every((c) => c === "")) continue;
    const w: ParsedWine = { type: "", name: "", mil: "", dom: "", prix: "", _typeOk: false };
    for (let j = 0; j < cells.length; j++) {
      const key = columnKeys[j];
      if (!key || key === "_typeOk") continue;
      (w as any)[key] = cells[j];
    }
    // Normalise type to a known id when possible (case insensitive, accept labels too)
    const tNorm = w.type.toLowerCase().replace(/s$/, "");
    if (SWEET_TYPE_SYNONYMS.has(tNorm)) {
      w.type = "blanc";
    } else {
      const direct = TYPE_IDS.find((id) => id === tNorm);
      const label = TYPES.find((t) => t.label.toLowerCase().replace(/s$/, "") === tNorm);
      w.type = direct || (label ? label.id : w.type);
    }
    w._typeOk = TYPE_IDS.includes(w.type);
    // Skip totally empty rows
    if (!w.name && !w.dom && !w.mil && !w.prix) continue;
    result.push(w);
  }
  return result;
}

// A small example file so the caviste knows exactly what columns/format to
// send, without having to guess from the on-screen hint.
function downloadTemplate() {
  const csv = [
    "type,name,mil,dom,prix",
    "rouge,Gevrey-Chambertin,2019,Rossignol-Trapet,68",
    "blanc,Meursault Les Narvaux,2020,Domaine Roulot,82",
    "rose,Bandol Rosé,2022,Domaine Tempier,34",
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modele-import-vins.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function WineImportModal({ onImport, onClose }: WineImportModalProps) {
  const [step, setStep] = useState<"intro" | "preview" | "importing" | "done">("intro");
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState<ParsedWine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (ext === "xlsx" || ext === "xls") {
      setError("Le format Excel n'est pas pris en charge directement. Dans Excel : Fichier → Enregistrer sous → CSV UTF-8, puis réimporte le .csv.");
      e.target.value = "";
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => {
      const text = (ev.target?.result as string) || "";
      setRaw(text);
      handleParse(text);
    };
    r.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const handleParse = (text?: string) => {
    setError(null);
    const source = text != null ? text : raw;
    const out = parseCsv(source);
    if (out.length === 0) {
      setError("Aucune ligne détectée. Vérifie que ton fichier contient bien les colonnes type, name, mil, dom, prix.");
      return;
    }
    setParsed(out);
    setStep("preview");
  };

  const validCount = parsed.filter((p) => p._typeOk && p.name && p.dom && p.mil && p.prix).length;

  const confirm = async () => {
    setStep("importing");
    setError(null);
    try {
      const valid = parsed
        .filter((p) => p._typeOk && p.name && p.dom && p.mil && p.prix)
        .map(({ _typeOk, ...rest }) => rest);
      const result = await onImport(valid);
      const nextCreatedCount = typeof result === "number" ? result : valid.length;
      setCreatedCount(nextCreatedCount);
      setStep("done");
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'import.");
      setStep("preview");
    }
  };

  const skippedDuplicates = createdCount !== null ? Math.max(0, validCount - createdCount) : 0;

  return (
    <Modal onClose={onClose} title="Importer des vins · CSV" wide>
      {step === "intro" && (
        <>
          <div className="text-sm text-secondary leading-[1.6] mb-3.5">
            Importe plusieurs vins d'un coup à partir d'un fichier CSV (ou en collant le contenu ci-dessous).
          </div>
          <div className="card p-3 mb-3.5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs tracking-[2px] text-tertiary uppercase">Colonnes attendues</div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="text-[11px] text-accent underline decoration-dotted underline-offset-2 cursor-pointer bg-transparent border-none p-0"
              >
                Télécharger un fichier modèle →
              </button>
            </div>
            <div className="grid grid-cols-5 gap-2 text-[12px]">
              {[
                { k: "type", h: "rouge / blanc / rose / effervescent" },
                { k: "name", h: "Appellation" },
                { k: "mil", h: "Millésime (ex: 2019)" },
                { k: "dom", h: "Domaine / producteur" },
                { k: "prix", h: "Prix (€)" },
              ].map((c) => (
                <div key={c.k}>
                  <div className="text-accent font-mono">{c.k}</div>
                  <div className="text-tertiary text-[10px] leading-[1.4] mt-0.5">{c.h}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-tertiary italic mt-2.5 leading-[1.5]">
              Une ligne d'en-tête est facultative. Séparateur : virgule ou point-virgule. Pour Excel, enregistre en CSV UTF-8 avant d'importer.
            </div>
          </div>

          <div className="mb-3">
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={6}
              placeholder={"type,name,mil,dom,prix\nrouge,Gevrey-Chambertin,2019,Rossignol-Trapet,68\nblanc,Meursault Les Narvaux,2020,Domaine Roulot,82"}
              className="input font-mono text-[12px] resize-y leading-[1.6]"
            />
          </div>

          {error && <div className="text-[12px] text-error mb-2">{error}</div>}

          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()} className="btn-secondary flex-1 py-2 text-sm">
              Choisir un fichier .csv
            </button>
            <Pill active onClick={() => handleParse()} disabled={!raw.trim()} style={{ flex: 2 }}>
              Analyser →
            </Pill>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xls" onChange={handleFile} className="hidden" />
        </>
      )}

      {step === "preview" && (
        <>
          <div className="text-sm text-secondary mb-2.5">
            {parsed.length} ligne{parsed.length > 1 ? "s" : ""} détectée{parsed.length > 1 ? "s" : ""} · {validCount} valide{validCount > 1 ? "s" : ""}
          </div>
          <div className="max-h-[280px] overflow-y-auto mb-3 border border-border rounded-md">
            {parsed.map((w, i) => {
              const missing: string[] = [];
              if (!w._typeOk) missing.push("type");
              if (!w.name) missing.push("name");
              if (!w.dom) missing.push("dom");
              if (!w.mil) missing.push("mil");
              if (!w.prix) missing.push("prix");
              const ok = missing.length === 0;
              return (
                <div key={i} className={`flex gap-2.5 px-3 py-2 items-center ${i < parsed.length - 1 ? "border-b border-foreground/[0.04]" : ""}`}>
                  <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TYPES.find((t) => t.id === w.type)?.color || "rgba(255,255,255,0.2)" }} />
                  <div className="flex-1 text-[12px] font-light">
                    <span className="text-foreground">{w.name || <em className="text-tertiary not-italic">— sans nom —</em>}</span>{" "}
                    <span className="text-tertiary">{w.mil}</span>
                    <div className="text-[10px] text-tertiary mt-0.5">{w.dom}</div>
                  </div>
                  {!ok && <span className="text-[10px] text-error shrink-0">Manque : {missing.join(", ")}</span>}
                  <div className="text-sm text-gold shrink-0">{w.prix ? `${w.prix}€` : ""}</div>
                </div>
              );
            })}
          </div>
          {error && <div className="text-[12px] text-error mb-2">{error}</div>}
          <div className="flex gap-2">
            <Pill onClick={() => { setStep("intro"); }} style={{ flex: 1 }}>← Modifier</Pill>
            <Pill active onClick={confirm} disabled={validCount === 0} style={{ flex: 2 }}>
              Ajouter {validCount} vin{validCount > 1 ? "s" : ""} à la cave →
            </Pill>
          </div>
        </>
      )}

      {step === "importing" && (
        <div className="py-6 text-center text-base text-secondary italic">Import en cours…</div>
      )}

      {step === "done" && (
        <>
          <div className="text-[12px] text-success mb-1.5 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" className="fill-success/15" />
              <path d="M4 7.2L6.2 9.5L10 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {createdCount ?? validCount} vin{(createdCount ?? validCount) > 1 ? "s" : ""} ajouté{(createdCount ?? validCount) > 1 ? "s" : ""} à la cave.
          </div>
          {skippedDuplicates > 0 && (
            <div className="text-[11px] text-tertiary italic mb-3">
              {skippedDuplicates} doublon{skippedDuplicates > 1 ? "s" : ""} déjà présent{skippedDuplicates > 1 ? "s" : ""} dans la cave, ignoré{skippedDuplicates > 1 ? "s" : ""}.
            </div>
          )}
          <Pill active onClick={onClose} style={{ width: "100%", marginTop: skippedDuplicates > 0 ? 0 : 12 }}>Fermer</Pill>
        </>
      )}
    </Modal>
  );
}
